import React, { useState } from 'react';
import WatchlistAssignGroupsModal from './WatchlistAssignGroupsModal';
import SearchableTicker from './SearchableTicker';
import ExpirationDateSelect from './Watchlist/ExpirationDateSelect';
import StrikePriceSelect from './Watchlist/StrikePriceSelect';
import apiClient from '../services/api'; // ✅ Import the Axios client
import { actionButtonTemplate } from '../theme';
import { isUsageLimitError, getUsageLimitData, isFeatureAccessError, getFeatureAccessData } from '../utils/usageLimitHelper';
import 'react-tooltip/dist/react-tooltip.css';
import UsageLimitModal from './UsageLimitModal';
import FeatureAccessModal from './FeatureAccessModal';

function WatchlistParamsForm({ groups = [], fetchGroups, fetchSavedContracts, onSimulationStart, onSimulationComplete }) {
  const [contracts, setContracts] = useState([
    {
      ticker: 'GOOG',
      direction: 'buy_call',
      option_type: 'call',
      position_type: 'buy',
      strike: 250,
      expiration: '2027-01-15',
      days_to_gain: '',
      number_of_contracts: '',
      average_cost_per_contract: '',
    },
  ]);

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [newlySavedContractIds, setNewlySavedContractIds] = useState([]);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);

  // --- Handle Contract Change ---
  const handleContractChange = (index, updatedFields) => {
    console.log(`✏ Updating contract at index ${index} with:`, updatedFields);
    setContracts(prev => {
      const updated = [...prev];

      // Clear dependent fields based on what changed
      // Ticker → clears expiration and strike
      if (updatedFields.ticker !== undefined && updatedFields.ticker !== updated[index].ticker) {
        console.log('🔄 Ticker changed, clearing expiration and strike');
        updated[index] = { ...updated[index], ...updatedFields, expiration: '', strike: '' };
      }
      // Direction → parse into option_type + position_type, clear strike
      else if (updatedFields.direction !== undefined && updatedFields.direction !== updated[index].direction) {
        const [posType, optType] = updatedFields.direction.split('_');
        console.log('🔄 Direction changed, clearing strike');
        updated[index] = { ...updated[index], ...updatedFields, position_type: posType, option_type: optType, strike: '' };
      }
      // Option Type → clears strike only (expiration can stay)
      else if (updatedFields.option_type !== undefined && updatedFields.option_type !== updated[index].option_type) {
        console.log('🔄 Option type changed, clearing strike');
        updated[index] = { ...updated[index], ...updatedFields, strike: '' };
      }
      // Expiration → clears strike only
      else if (updatedFields.expiration !== undefined && updatedFields.expiration !== updated[index].expiration) {
        console.log('🔄 Expiration changed, clearing strike');
        updated[index] = { ...updated[index], ...updatedFields, strike: '' };
      }
      // No dependencies changed, just update the field
      else {
        updated[index] = { ...updated[index], ...updatedFields };
      }

      console.log('📦 Updated Contracts State:', updated);
      return updated;
    });
  };

  // --- Add New Contract ---
  const handleAddContract = () => {
    console.log('➕ Adding new empty contract.');
    setContracts(prev => [
      ...prev,
      {
        ticker: '',
        direction: 'buy_call',
        option_type: 'call',
        position_type: 'buy',
        strike: '',
        expiration: '',
        days_to_gain: '',
        number_of_contracts: '',
        average_cost_per_contract: '',
      },
    ]);
    console.log('📦 Current Contracts State After Add:', contracts);
  };

  // --- Remove Contract ---
  const handleRemoveContract = (index) => {
    console.log(`🗑 Removing contract at index ${index}:`, contracts[index]);
    setContracts(prev => {
      const updated = prev.filter((_, i) => i !== index);
      console.log('📦 Updated Contracts After Remove:', updated);
      return updated;
    });
  };

  // --- Duplicate Contract ---
  const handleDuplicateContract = (index) => {
    setContracts(prev => {
      const source = prev[index];
      const duplicate = {
        ticker: source.ticker,
        direction: source.direction,
        option_type: source.option_type,
        position_type: source.position_type,
        strike: source.strike,
        expiration: source.expiration,
        days_to_gain: source.days_to_gain,
        number_of_contracts: source.number_of_contracts,
        average_cost_per_contract: source.average_cost_per_contract,
      };
      return [
        ...prev.slice(0, index + 1),
        duplicate,
        ...prev.slice(index + 1),
      ];
    });
  };

  // --- Run Simulator ---
  const runSimulatorAndGetResults = async (inputContracts) => {
    console.log('🚀 Preparing contracts for simulation:', inputContracts);

    const sanitizedContracts = inputContracts.map(c => {
      const contract = {
        ticker: c.ticker,
        option_type: c.option_type,
        position_type: c.position_type || 'buy',
        strike: parseFloat(c.strike),
        expiration: c.expiration,
      };
      if (c.days_to_gain) contract.days_to_gain = parseInt(c.days_to_gain);
      if (c.number_of_contracts) contract.number_of_contracts = parseInt(c.number_of_contracts);
      if (c.average_cost_per_contract) contract.average_cost_per_contract = parseFloat(c.average_cost_per_contract);
      return contract;
    });

    console.log('🧹 Sanitized Contracts:', sanitizedContracts);

    const response = await apiClient.post('/run-watchlist/', { contracts: sanitizedContracts });
    console.log('✅ API Response from /run-watchlist/:', response.data);

    return response.data;
  };

  const handleRunSimulator = async () => {
    try {
      console.log('▶ Running watchlist simulator with contracts:', contracts);

      // Start loading state with contract count
      if (onSimulationStart) {
        onSimulationStart(contracts.length);
      }

      const results = await runSimulatorAndGetResults(contracts);
      console.log('🎯 Simulation Results:', results);
      onSimulationComplete(results);
      console.log('📊 Watchlist Table updated with simulation results.');
      // Removed success alert - silent success with auto-scroll
    } catch (error) {
      console.error('❌ Error running simulator:', error);
      if (isUsageLimitError(error)) {
        setUsageLimitData(getUsageLimitData(error));
      } else if (isFeatureAccessError(error)) {
        setFeatureAccessData(getFeatureAccessData(error));
      } else {
        alert('Failed to run simulator.');
      }
    }
  };

  // --- Save Contracts ---
  const handleSaveContracts = async () => {
    console.log('💾 Saving contracts:', contracts);
    if (contracts.some(c => !c.ticker || !c.strike || !c.expiration)) {
      console.warn('⚠ Missing required fields in one or more contracts.');
      alert("Please ensure all contracts have a Ticker, Strike, and Expiration date.");
      return;
    }

    try {
      const savedIds = [];
      for (const contract of contracts) {
        const contractData = {
          ticker: contract.ticker,
          option_type: contract.option_type,
          position_type: contract.position_type || 'buy',
          strike: parseFloat(contract.strike),
          expiration: contract.expiration,
        };
        if (contract.days_to_gain) contractData.days_to_gain = parseInt(contract.days_to_gain);
        if (contract.number_of_contracts) contractData.number_of_contracts = parseInt(contract.number_of_contracts);
        if (contract.average_cost_per_contract) contractData.average_cost_per_contract = parseFloat(contract.average_cost_per_contract);

        console.log('📤 Saving contract to /saved-contracts/:', contractData);
        const response = await apiClient.post('/saved-contracts/', contractData);
        console.log('✅ Saved Contract Response:', response.data);
        savedIds.push(response.data.id);
      }

      setNewlySavedContractIds(savedIds);
      console.log('📦 Newly Saved Contract IDs:', savedIds);
      setAssignModalVisible(true);
    } catch (error) {
      console.error('❌ Error saving contracts:', error);
      alert(`A problem occurred while saving: ${error.message}`);
    }
  };

  // --- Assign Contracts to Groups ---
  const handleAssignToGroups = async (selectedGroupIds) => {
    console.log('🔗 Assigning contracts to groups:', selectedGroupIds);
    if (selectedGroupIds.length === 0) {
      alert("No groups selected. Closing.");
      setAssignModalVisible(false);
      return;
    }

    try {
      for (const groupId of selectedGroupIds) {
        console.log(`📤 Assigning to group ${groupId} with contracts:`, newlySavedContractIds);
        await apiClient.post(`/watchlist-groups/${groupId}/assign-contracts/`, {
          contract_ids: newlySavedContractIds,
        });
      }

      console.log('✅ Contracts saved and assigned successfully.');
      setNewlySavedContractIds([]);

      console.log('🔄 Fetching latest groups and saved contracts.');
      await fetchGroups();
      await fetchSavedContracts();
    } catch (error) {
      console.error('❌ Error assigning contracts to groups:', error);
      alert(`Failed to assign contracts: ${error.message}`);
    }
  };

  return (
    <div className="space-y-4 mb-6">
      <h3 className="heading-lg text-green-400">Watchlist Contracts</h3>

      {contracts.map((contract, index) => (
        <div key={index} className="card space-y-3">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold text-brand-blue">Contract {index + 1}</span>
            <div className="flex gap-2">
              <button onClick={() => handleDuplicateContract(index)} className="btn-ghost-primary"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Copy this contract's settings into a new row below."
              >Duplicate</button>
              <button
                onClick={() => handleRemoveContract(index)}
                className="btn-ghost-danger"
                disabled={contracts.length <= 1}
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Remove this contract row from the form. Does not delete saved contracts."
                style={contracts.length <= 1 ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
              >Remove</button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="filter-heading">TICKER</label>
              <SearchableTicker
                value={contract.ticker ? { label: contract.ticker, value: contract.ticker } : null}
                onChange={(selected) => handleContractChange(index, { ticker: selected?.value || '' })}
              />
            </div>

            <div>
              <label className="filter-heading cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Buy Calls = bullish long. Buy Puts = bearish long. Sell Puts = bullish short. Sell Calls = bearish short. Selling options to open a position carries higher risk without proper knowledge — please research before trading."
              >DIRECTION</label>
              <select
                className="input"
                value={contract.direction}
                onChange={(e) => handleContractChange(index, { direction: e.target.value })}
              >
                <option value="buy_call">Buy Calls</option>
                <option value="buy_put">Buy Puts</option>
                <option value="sell_put">Sell Puts</option>
                <option value="sell_call">Sell Calls</option>
              </select>
            </div>

            <div>
              <label className="filter-heading cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="The option's expiration date. Select a ticker first to load available dates."
              >
                EXPIRATION
                <span className="text-xs text-gray-400 ml-2">(requires ticker)</span>
              </label>
              <ExpirationDateSelect
                ticker={contract.ticker}
                value={contract.expiration}
                onChange={(newExpiration) => handleContractChange(index, { expiration: newExpiration })}
                disabled={false}
              />
            </div>

            <div>
              <label className="filter-heading cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="The strike price for the option. Select ticker, direction, and expiration first to load available strikes."
              >
                STRIKE
                <span className="text-xs text-gray-400 ml-2">(requires ticker, type & expiration)</span>
              </label>
              <StrikePriceSelect
                ticker={contract.ticker}
                optionType={contract.option_type}
                expiration={contract.expiration}
                value={contract.strike}
                onChange={(newStrike) => handleContractChange(index, { strike: newStrike })}
                disabled={false}
              />
            </div>

            <div>
              <label className="filter-heading cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Your planned holding period in days. Used for simulation and annualized return calculations. Defaults to 50% of days until expiration."
              >DAYS TO EXIT <span className="text-gray-400">(optional)</span></label>
              <input
                type="number"
                className="input"
                value={contract.days_to_gain || ''}
                onChange={(e) => handleContractChange(index, { days_to_gain: e.target.value })}
              />
              {/* Bottom spacer to match strike field's helper text area */}
              <div className="h-5 mt-1"></div>
            </div>

            <div>
              <label className="filter-heading">NUMBER OF CONTRACTS <span className="text-gray-400">(optional)</span></label>
              <input
                type="number"
                className="input"
                value={contract.number_of_contracts || ''}
                onChange={(e) => handleContractChange(index, { number_of_contracts: e.target.value })}
              />
              {/* Bottom spacer to match strike field's helper text area */}
              <div className="h-5 mt-1"></div>
            </div>

            <div>
              <label className="filter-heading cursor-help"
                data-tooltip-id="watchlist-card-tooltip"
                data-tooltip-content="Your average purchase price per contract (per share). Used for P&L and equity tracking. Defaults to current premium if not set."
              >AVG COST PER CONTRACT <span className="text-gray-400">(optional)</span></label>
              <input
                type="number"
                className="input"
                value={contract.average_cost_per_contract || ''}
                onChange={(e) => handleContractChange(index, { average_cost_per_contract: e.target.value })}
              />
            </div>
          </div>

          {contracts.length <= 1 && (
            <p className="text-sm text-gray-400 mt-1">
              This is your only contract. Change the parameters above to start fresh.
            </p>
          )}
        </div>
      ))}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleRunSimulator}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary} flex-1`}
          data-tooltip-id="watchlist-card-tooltip"
          data-tooltip-content="Run a simulation on the contracts above without saving them. Results appear in the table below."
        >
          Run Watchlist Simulator
        </button>
        <button
          type="button"
          onClick={handleAddContract}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary}`}
          data-tooltip-id="watchlist-card-tooltip"
          data-tooltip-content="Add a new empty contract row to the form."
        >
          + New Contract
        </button>
        <button
          type="button"
          onClick={handleSaveContracts}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.success}`}
          data-tooltip-id="watchlist-card-tooltip"
          data-tooltip-content="Save all contracts and assign them to watchlist groups for ongoing tracking."
        >
          Save
        </button>
      </div>

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}

      <WatchlistAssignGroupsModal
        isOpen={assignModalVisible}
        onClose={() => setAssignModalVisible(false)}
        groups={groups}
        fetchGroups={fetchGroups}
        contracts={newlySavedContractIds}
        contractDetails={contracts.map(c => ({
          ticker: c.ticker,
          strike: c.strike,
          expiration: c.expiration,
          numberOfContracts: c.number_of_contracts || 1
        }))}
        onAssign={handleAssignToGroups}
      />
    </div>
  );
}

export default WatchlistParamsForm;
